// https://github.com/therealparmesh/object-to-formdata

// MIT License

// Copyright (c) 2016

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

declare type ParamType = undefined | null;
interface ICfg {
	arrayBrackets?: boolean;
	nullEmptyBrackets?: boolean;
	indices?: boolean;
	nulls?: boolean;
}

function isUndefined(value: any): value is undefined {
	return value === undefined;
}

function isNull(value: any): value is null {
	return value === null;
}

function isObject(value: any): boolean {
	return value === Object(value);
}

function isArray(value: any): value is any[] {
	return Array.isArray(value);
}

function isDate(value: any): value is Date {
	return value instanceof Date;
}

function isBlob(value: any): boolean {
	return (
		value &&
		typeof value.size === 'number' &&
		typeof value.type === 'string' &&
		typeof value.slice === 'function'
	);
}

function isFile(value: any): boolean {
	return (
		isBlob(value) &&
		(typeof value.lastModifiedDate === 'object' ||
			typeof value.lastModified === 'number') &&
		typeof value.name === 'string'
	);
}

function isFormData(value: any): value is FormData {
	return value instanceof FormData;
}

export function objectToFormData(obj: any, cfg?: ICfg | FormData | ParamType, fd?: FormData, pre?: FormData | string): FormData {
	if (isFormData(cfg)) {
		pre = fd;
		fd = cfg;
		cfg = null;
	}

	return objectToFormDataImpl(obj, (cfg || {}) as ICfg, fd || new FormData(), pre as string);
}

function objectToFormDataImpl(obj: any, cfg: ICfg, fd: FormData, pre: string): FormData {
	cfg.arrayBrackets = isUndefined(cfg.arrayBrackets) ? false : cfg.arrayBrackets;
	cfg.nullEmptyBrackets = isUndefined(cfg.nullEmptyBrackets) ? true : cfg.nullEmptyBrackets;
	cfg.indices = isUndefined(cfg.indices) ? false : cfg.indices;
	cfg.nulls = isUndefined(cfg.nulls) ? true : cfg.nulls;

	if (isUndefined(obj)) {
		return fd;
	} else if (isNull(obj)) {
		if (cfg.nulls) {
			fd.append(pre, '');
		}
	} else if (isArray(obj)) {
		if (!obj.length && cfg.nullEmptyBrackets) {
			// nothing to add

		} else if (!obj.length) {
			const key = pre + '[]';

			fd.append(key, '');
		} else {
			obj.forEach((value, index) => {
				const key = cfg.arrayBrackets ? pre + '[' + (cfg.indices ? index : '') + ']' : pre;

				objectToFormData(value, cfg, fd, key);
			});
		}
	} else if (isDate(obj)) {
		fd.append(pre, obj.toISOString());
	} else if (isObject(obj) && !isFile(obj) && !isBlob(obj)) {
		Object.keys(obj).forEach(prop => {
			const value = obj[prop];

			if (isArray(value)) {
				while (prop.length > 2 && prop.lastIndexOf('[]') === prop.length - 2) {
					prop = prop.substring(0, prop.length - 2);
				}
			}

			const key = pre ? pre + '[' + prop + ']' : prop;

			objectToFormData(value, cfg, fd, key);
		});
	} else {
		fd.append(pre, obj);
	}

	return fd;
}
